import {useQuery} from 'react-query'
import { useAuth } from '../components/auth/AuthContext'

const endpointUrl = process.env.REACT_APP_ENDPOINT_STATUS

export enum VerificationStatus {
  inProgress = 'IN_PROGRESS',
  verified = 'VERIFIED',
  missingInformation = 'MISSING_INFORMATION',
  error = 'ERROR',
}

type StatusInfo = {
  status: VerificationStatus
}

// Keep in sync with: https://github.com/MindTheCoin/id-microservices/blob/master/register/src/register-status.ts
const serverStatusToVerificationStatus: {[key: number]: VerificationStatus} = {
  0: VerificationStatus.verified,
  1: VerificationStatus.inProgress,
  2: VerificationStatus.inProgress,
  3: VerificationStatus.error,
  4: VerificationStatus.missingInformation,
  10: VerificationStatus.error
}

type ServerResponse = {
  status: keyof typeof serverStatusToVerificationStatus,
  success: boolean
}

export async function getStatus(
  token: string,
  tokenType: string,
): Promise<StatusInfo> {
  if (!endpointUrl || !token || !tokenType) {
    throw new Error('getStatus: missing status endpoint or token information')
  }

  try {
    const response = await fetch(endpointUrl, {
      method: 'GET',
      headers: {
        Authorization: `${tokenType} ${token}`
      }
    })

    if (!response.ok) {
      return {status: VerificationStatus.error}
    }

    const serverResponse: ServerResponse = await response.json()

    const verificationStatus = serverStatusToVerificationStatus[serverResponse.status] || VerificationStatus.error
    return {status: verificationStatus}
  } catch (error) {
    throw new Error('getStatus: failed to parse response')
  }

  /*
    // Mocks
    throw new Error('rejected')
    await new Promise((resolve) => setTimeout(resolve, 2000))
    return {status: VerificationStatus.inProgress}
    return {status: VerificationStatus.error}
    return {status: VerificationStatus.verified}
  */
}

export function useStatus(refetchInterval?: number) {
  const {
    tokenInfo
  } = useAuth()
  const {token, tokenType} = tokenInfo || {}

  return useQuery<StatusInfo | null>(
    ['status', token],
    () => {
      if (!token || !tokenType) return null

      try {
        return getStatus(token, tokenType)
      } catch (error) {
        throw Error
      }
    },
    {
      enabled: !!(token && tokenType),
      refetchInterval: refetchInterval,
      keepPreviousData: true
    },
  )
}
